/* Progressive presentation only: move existing description nodes, never copy product facts. */
(() => {
  if (customElements.get('moonsmc-product-details')) return;
  const labelPattern = /^(?:features?|specifications?|details?|product details|fitment(?:\s*&\s*installation)?|installation|sizing|size guide|care instructions)\s*:?$/i;
  const fitPattern = /fitment|installation|\binstall notes?\b|sizing|size guide|fit note|size\s*&\s*display/i;
  const factsPattern = /features?|specifications?|details|what.s included/i;
  const mediaSelector = 'a, img, iframe, video, table, form, button, deferred-media, template';
  const clean = (text) => text.replace(/\s+/g, ' ').trim();
  const element = (tag, className) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  };
  const isHeading = (node) => node.nodeType === 1 && /^(H2|H3|H4)$/.test(node.tagName);
  const isCard = (node) => node.nodeType === 1 && node.tagName === 'DIV' &&
    /border[^:;]*:/i.test(node.getAttribute('style') || '') &&
    !!node.querySelector('strong, b, [style*="font-weight"]') && !node.querySelector(mediaSelector);

  function flattenPresentationWrappers(host) {
    Array.from(host.children).forEach((node) => {
      if (!/^(DIV|SECTION)$/.test(node.tagName) || node.id ||
          !Array.from(node.children).some(isHeading)) return;
      while (node.firstChild) host.insertBefore(node.firstChild, node);
      node.remove();
    });
  }

  function promoteLegacyHeadings(host) {
    Array.from(host.children).forEach((node) => {
      if (node.matches('strong, b') && labelPattern.test(clean(node.textContent))) {
        const heading = element('h3');
        node.before(heading);
        heading.append(node);
      } else if (node.tagName === 'P') {
        const label = node.querySelector('strong, b');
        if (!label || !labelPattern.test(clean(label.textContent))) return;
        // A bold phrase is a heading only at the beginning of the paragraph.
        const prefix = node.textContent.slice(0, node.textContent.indexOf(label.textContent));
        if (clean(prefix)) return;
        const heading = element('h3');
        node.before(heading);
        heading.append(label);
      }
    });
  }

  function addIcon(card) {
    const title = card.querySelector('strong, b, [style*="font-weight"]');
    title?.classList.add('mm-details-feature-title');
    const text = clean(title?.textContent || '');
    let drawing = '<path d="m12 3 8 4v10l-8 4-8-4V7l8-4Z"/><path d="m4 7 8 5 8-5M12 12v9"/>';
    if (/light|beam|lumens|led|bright/i.test(text)) drawing = '<path d="M14 4c-7 0-11 4-11 8s4 8 11 8V4ZM18 5h4M18 10h4M18 15h4M18 20h4"/>';
    else if (/fit|install|plug|hardware/i.test(text)) drawing = '<path d="m14 6 4 4M12 3l2 3-9 9-3 1 1-3 9-10ZM15 13l6 6-2 2-6-6M16 3a5 5 0 0 0 5 5"/>';
    else if (/fabric|cotton|tee|stretch|comfort|wear|palm|material/i.test(text)) drawing = '<path d="m8 3-5 3-2 6 5 2 1-3v10h10V11l1 3 5-2-2-6-5-3a4 4 0 0 1-8 0Z"/>';
    else if (/seal|protect|durab|coat|weather|housing/i.test(text)) drawing = '<path d="m12 3 8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z"/><path d="m8 12 3 3 5-6"/>';
    const icon = element('span', 'mm-details-feature-icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" focusable="false">${drawing}</svg>`;
    card.prepend(icon);
    // Retain decorative emoji nodes, but let one consistent outline icon do the visual work.
    const walker = document.createTreeWalker(card, 4);
    const decorative = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.parentElement?.closest('svg')) continue;
      if (node.textContent.trim() && !/[\p{L}\p{N}]/u.test(node.textContent) && /\p{Extended_Pictographic}/u.test(node.textContent)) decorative.push(node);
    }
    decorative.forEach((node) => {
      const span = element('span', 'mm-details-original-emoji');
      span.setAttribute('aria-hidden', 'true');
      node.before(span);
      span.append(node);
    });
  }

  function groupInitialFeatures(host, featureHeading) {
    const children = Array.from(host.children);
    let cards = [];
    for (const node of children) {
      if (isHeading(node) && node.tagName !== 'H2') break;
      if (isCard(node)) {
        let next = node;
        while (next && isCard(next)) {
          cards.push(next);
          next = next.nextElementSibling;
        }
        break;
      }
      if (node.tagName === 'DIV' && /display\s*:\s*grid/i.test(node.getAttribute('style') || '')) {
        const candidates = Array.from(node.children);
        if (candidates.length && candidates.every(isCard)) {
          cards = candidates;
          break;
        }
      }
    }
    if (!cards.length) return;
    const first = cards[0];
    const band = element('section', 'mm-details-feature-band');
    const heading = element('h3', 'mm-details-feature-band__heading');
    heading.textContent = featureHeading || 'Key features';
    heading.dataset.moonsmcGenerated = 'true';
    const grid = element('div', 'mm-details-feature-grid');
    grid.dataset.columns = String(cards.length === 1 ? 1 : cards.length === 2 || cards.length === 4 ? 2 : 3);
    first.before(band);
    band.append(heading, grid);
    cards.forEach((card) => {
      card.classList.add('mm-details-feature');
      grid.append(card);
      addIcon(card);
    });
    // The V4 source already has a grid wrapper; preserve its position without its old sizing rules.
    if (band.parentElement !== host) {
      const wrapper = band.parentElement;
      wrapper.classList.add('mm-details-feature-wrapper');
      wrapper.before(band);
    }
  }

  function styleFactLines(panel) {
    // Preserve original line-break content and text nodes; display each factual line as a row.
    const containers = [panel, ...panel.querySelectorAll('p')];
    containers.forEach((container) => {
      const children = Array.from(container.childNodes);
      if (children.filter((node) => node.nodeName === 'BR').length < 3 || container.querySelector(mediaSelector)) return;
      let row;
      for (const node of children) {
        if (isHeading(node)) { row = undefined; continue; }
        if (node.nodeType === 1 && !['BR', 'SPAN', 'STRONG', 'B', 'EM', 'I'].includes(node.tagName)) { row = undefined; continue; }
        if (!row) {
          row = element('span', 'mm-details-fact-line');
          node.before(row);
        }
        row.append(node);
        if (node.nodeName === 'BR') row = undefined;
      }
    });
  }

  function arrangeSections(host) {
    const nodes = Array.from(host.childNodes);
    const groups = [];
    let current;
    let overview;
    for (const node of nodes) {
      if (node.nodeType === 1 && node.matches('.mm-details-feature-band')) {
        current = undefined;
        continue;
      }
      if (isHeading(node) && (node.tagName !== 'H2' || groups.length || overview)) {
        current = element('section', 'mm-details-panel');
        const heading = clean(node.textContent);
        if (fitPattern.test(heading)) current.classList.add('mm-details-panel--fitment');
        if (factsPattern.test(heading)) current.classList.add('mm-details-panel--facts');
        node.before(current);
        current.append(node);
        groups.push(current);
        continue;
      }
      if (!current && !groups.length && !(node.nodeType === 3 && !node.textContent.trim())) {
        if (!overview) {
          overview = element('div', 'mm-details-overview');
          node.before(overview);
        }
        overview.append(node);
      } else if (current) current.append(node);
    }
    groups.forEach((panel) => {
      if (panel.classList.contains('mm-details-panel--facts')) styleFactLines(panel);
    });
    if (groups.length < 2) return;
    const columns = element('div', 'mm-details-columns');
    const left = element('div', 'mm-details-column');
    const right = element('div', 'mm-details-column');
    groups[0].before(columns);
    columns.append(left, right);
    const weights = groups.map((panel) => Math.max(180, clean(panel.textContent).length) + panel.querySelectorAll('img, iframe, video, table').length * 500);
    const target = weights.reduce((sum, weight) => sum + weight, 0) / 2;
    let sum = 0;
    let split = 1;
    let best = Infinity;
    weights.slice(0, -1).forEach((weight, index) => {
      sum += weight;
      if (Math.abs(target - sum) < best) { best = Math.abs(target - sum); split = index + 1; }
    });
    groups.forEach((panel, index) => (index < split ? left : right).append(panel));
  }

  class MoonsmcProductDetails extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enhanced || this.dataset.layout === 'fly-eye') return;
      this.dataset.enhanced = 'true';
      const wrapper = this.children.length === 1 && this.firstElementChild.matches('section.moonsmc-product') ? this.firstElementChild : this;
      wrapper.classList.add('mm-details-source');
      flattenPresentationWrappers(wrapper);
      promoteLegacyHeadings(wrapper);
      groupInitialFeatures(wrapper, this.dataset.featureHeading);
      arrangeSections(wrapper);
      this.querySelectorAll('table').forEach((table) => {
        const scroll = element('div', 'mm-details-table-scroll');
        scroll.setAttribute('tabindex', '0');
        scroll.setAttribute('role', 'region');
        scroll.setAttribute('aria-label', 'Product information table');
        table.before(scroll);
        scroll.append(table);
      });
      this.classList.add('mm-details-ready');
    }
  }
  customElements.define('moonsmc-product-details', MoonsmcProductDetails);
})();
